import React from "react";
import { useFormikContext } from "formik";
import useAxios from "axios-hooks";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import { Checkbox, MessageBar, MessageBarType } from "@fluentui/react";
import TextField from "../../../../Forms/TextField";

export const enableContentType = [9, 10, 11, 12, 13, 14, 15, 16, 86];
export const enableCourse = [18, 19];
export const enableMinimumScore = [18];
export const enableJournalPrompt = [93];

// Must match ChecklistConstants.JournalPromptTitlePrefix / JournalPromptBriefMaxLength.
export const journalPromptTitlePrefix = "Journal about: ";
export const journalPromptBriefMaxLength = 60;

const ProprietarySpecificFields = () => {
  const { getFieldProps } = useFormikContext();

  const [
    {
      data: proprietarySpecificActionTypeData,
      loading: proprietarySpecificActionTypeDataLoading,
    },
  ] = useAxios("action-items/proprietary-specific-action-types");

  const pointsId = Number(getFieldProps("proprietarySpecificActionType").value);

  const isProprietarySpecificContentFieldEnabled = enableContentType.includes(
    pointsId
  );

  const isProprietarySpecificCourseFieldEnabled = enableCourse.includes(
    pointsId
  );

  const isMinimumScoreEnabled = enableMinimumScore.includes(pointsId);

  const isJournalPromptEnabled = enableJournalPrompt.includes(pointsId);

  const point =
    proprietarySpecificActionTypeData &&
    proprietarySpecificActionTypeData.filter((points) => {
      return points.pointsID === pointsId;
    });

  return (
    <>
      <ProprietarySpecificActionTypeField
        data={proprietarySpecificActionTypeData}
        loading={proprietarySpecificActionTypeDataLoading}
      />

      {isProprietarySpecificContentFieldEnabled &&
        !proprietarySpecificActionTypeDataLoading &&
        proprietarySpecificActionTypeData && (
          <ProprietarySpecificContentField
            proprietarySpecificContentSlug={point && point[0]?.slug}
          />
        )}

      {isProprietarySpecificCourseFieldEnabled &&
        !proprietarySpecificActionTypeDataLoading &&
        proprietarySpecificActionTypeData && (
          <ProprietarySpecificCourseField />
        )}

      {isMinimumScoreEnabled && (
        <MinimumScoreField />
      )}

      {isJournalPromptEnabled && <JournalPromptFields />}
    </>
  );
};

const ProprietarySpecificActionTypeField = ({ data, loading }) => {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const options =
    data &&
    data.map((proprietarySpecificActionType) => ({
      value: proprietarySpecificActionType.pointsID,
      label: proprietarySpecificActionType.name,
    }));

  const currentValue = getFieldProps("proprietarySpecificActionType").value;

  return (
    <div>
      <label className="ms-Label root-113" style={{ fontWeight: 600, padding: "5px 0" }}>
        Action <span style={{ color: "rgb(164, 38, 44)" }}>*</span>
      </label>
      <Select
        components={{ MenuList: CustomMenuList }}
        options={options || []}
        placeholder="Select an option"
        value={options?.find((option) => option.value === currentValue) || null}
        onChange={(option) => setFieldValue("proprietarySpecificActionType", option?.value)}
        isSearchable
        isDisabled={loading}
        styles={selectStyles}
      />
    </div>
  );
};

const CustomMenuList = ({ children, ...props }) => {
  const height = 35;
  const itemCount = Array.isArray(children) ? children.length : 0;
  const totalHeight = Math.min(300, itemCount * height);

  if (!Array.isArray(children) || itemCount === 0) {
    return <div>{children}</div>;
  }

  return (
    <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
      {({ index, style }) => (
        <div
          style={{
            ...style,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0 8px",
            boxSizing: "border-box",
          }}
          title={children[index]?.props?.children}
        >
          {children[index]}
        </div>
      )}
    </List>
  );
};

const selectStyles = {
  menuList: (base) => ({
    ...base,
    maxHeight: "300px",
    overflowY: "auto",
  }),
  container: (base) => ({
    ...base,
    width: "100%",
  }),
  control: (base) => ({
    ...base,
    border: "1px solid #aaaaaa",
  }),
};

const DetailOptionLabel = ({ id, slug, title }) => (
  <div style={{ lineHeight: 1.3 }}>
    <div style={{ fontWeight: 600 }}>{title}</div>
    <div style={{ fontSize: 11, color: '#666' }}>
      ID: {id} &nbsp;|&nbsp; {slug}
    </div>
  </div>
);

const detailSelectStyles = {
  ...selectStyles,
  option: (base) => ({
    ...base,
    padding: '6px 12px',
  }),
  singleValue: (base) => ({
    ...base,
    lineHeight: 1.3,
  }),
};

const DetailMenuList = ({ children }) => {
  if (!Array.isArray(children) || children.length === 0) {
    return <div>{children}</div>;
  }

  return (
    <div style={{ maxHeight: 350, overflowY: 'auto' }}>
      {children}
    </div>
  );
};

const ProprietarySpecificContentField = ({
  proprietarySpecificContentSlug,
}) => {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const [
    {
      data: proprietarySpecificContentData,
      loading: proprietarySpecificContentDataLoading,
      error: proprietarySpecificContentDataError,
    },
  ] = useAxios({
    url: `/resources/content/titles?TypeSlug=${proprietarySpecificContentSlug}`,
    useCache: true,
  });

  const options =
    proprietarySpecificContentData &&
    Array.isArray(proprietarySpecificContentData) &&
    proprietarySpecificContentData.map((item) => ({
      value: item.contentId,
      label: item.title,
      contentId: item.contentId,
      slug: item.slug,
      title: item.title,
    }));

  const currentValue = getFieldProps("proprietarySpecificContent").value;

  return (
    <div>
      <label className="ms-Label root-113" style={{ fontWeight: 600, padding: "5px 0" }}>
        Content <span style={{ color: "rgb(164, 38, 44)" }}>*</span>
      </label>
      <Select
        components={{ MenuList: DetailMenuList }}
        options={options || []}
        placeholder="Select an option"
        value={options?.find((option) => option.value === currentValue) || null}
        onChange={(option) => setFieldValue("proprietarySpecificContent", option?.value)}
        formatOptionLabel={(option) => (
          <DetailOptionLabel id={option.contentId} slug={option.slug} title={option.title} />
        )}
        noOptionsMessage={() =>
          proprietarySpecificContentDataError
            ? "Error loading content"
            : proprietarySpecificContentDataLoading
            ? "Loading..."
            : "No content available"
        }
        isSearchable
        isLoading={proprietarySpecificContentDataLoading}
        isDisabled={proprietarySpecificContentDataLoading}
        styles={detailSelectStyles}
      />
    </div>
  );
};

const ProprietarySpecificCourseField = () => {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const [
    {
      data: proprietarySpecificCourseData,
      loading: proprietarySpecificCourseDataLoading,
      error: proprietarySpecificCourseDataError,
    },
  ] = useAxios({
    url: `resources/courses/all/titles`,
    useCache: true,
  });

  const options =
    proprietarySpecificCourseData &&
    Array.isArray(proprietarySpecificCourseData) &&
    proprietarySpecificCourseData.map((item) => ({
      value: item.crsId,
      label: item.title,
      crsId: item.crsId,
      slug: item.slug,
      title: item.title,
    }));

  const currentValue = getFieldProps("proprietarySpecificCourse").value;

  return (
    <div>
      <label className="ms-Label root-113" style={{ fontWeight: 600, padding: "5px 0" }}>
        Courses <span style={{ color: "rgb(164, 38, 44)" }}>*</span>
      </label>
      <Select
        components={{ MenuList: DetailMenuList }}
        options={options || []}
        placeholder="Select an option"
        value={options?.find((option) => option.value === currentValue) || null}
        onChange={(option) => setFieldValue("proprietarySpecificCourse", option?.value)}
        formatOptionLabel={(option) => (
          <DetailOptionLabel id={option.crsId} slug={option.slug} title={option.title} />
        )}
        noOptionsMessage={() =>
          proprietarySpecificCourseDataError
            ? "Error loading courses"
            : proprietarySpecificCourseDataLoading
            ? "Loading..."
            : "No courses available"
        }
        isSearchable
        isLoading={proprietarySpecificCourseDataLoading}
        isDisabled={proprietarySpecificCourseDataLoading}
        styles={detailSelectStyles}
      />
    </div>
  );
};

const JournalPromptFields = () => {
  const { getFieldProps, setFieldValue } = useFormikContext();

  const [{ data, loading, error }] = useAxios({
    url: "journal/categoriesPromptsAudit",
    useCache: true,
  });

  const categories = data?.categories || [];
  const prompts = data?.prompts || [];

  const selectedPromptId = getFieldProps("proprietarySpecificJournalPrompt").value;
  const selectedPrompt = prompts.find((p) => p.journalPromptId === selectedPromptId);
  const brief = getFieldProps("journalPromptBrief").value;

  // Category isn't persisted on the action item, it's derived from the chosen prompt.
  const selectedCategoryId =
    getFieldProps("journalPromptCategory").value ?? selectedPrompt?.journalCategoryId ?? null;

  const categoryOptions = categories.map((c) => ({
    value: c.journalCategoryId,
    label: c.isHidden ? `${c.category} (hidden)` : c.category,
  }));

  const promptOptions = prompts
    .filter((p) => p.journalCategoryId === selectedCategoryId)
    .map((p) => ({
      value: p.journalPromptId,
      label: p.prompt,
      journalPromptId: p.journalPromptId,
      prompt: p.prompt,
      checklistOnly: p.checklistOnly,
    }));

  const showReuseWarning = !!selectedPrompt && !selectedPrompt.checklistOnly;

  return (
    <>
      <div>
        <label className="ms-Label root-113" style={{ fontWeight: 600, padding: "5px 0" }}>
          Journal Category <span style={{ color: "rgb(164, 38, 44)" }}>*</span>
        </label>
        <Select
          options={categoryOptions}
          placeholder="Select an option"
          value={categoryOptions.find((option) => option.value === selectedCategoryId) || null}
          onChange={(option) => {
            setFieldValue("journalPromptCategory", option?.value ?? null);
            setFieldValue("proprietarySpecificJournalPrompt", null);
          }}
          noOptionsMessage={() =>
            error ? "Error loading journal categories" : loading ? "Loading..." : "No categories available"
          }
          isSearchable
          isLoading={loading}
          isDisabled={loading}
          styles={selectStyles}
        />
      </div>

      <div>
        <label className="ms-Label root-113" style={{ fontWeight: 600, padding: "5px 0" }}>
          Journal Prompt <span style={{ color: "rgb(164, 38, 44)" }}>*</span>
        </label>
        <Select
          components={{ MenuList: DetailMenuList }}
          options={promptOptions}
          placeholder={selectedCategoryId ? "Select an option" : "Select a category first"}
          value={promptOptions.find((option) => option.value === selectedPromptId) || null}
          onChange={(option) => setFieldValue("proprietarySpecificJournalPrompt", option?.value)}
          formatOptionLabel={(option) => (
            <DetailOptionLabel
              id={option.journalPromptId}
              slug={option.checklistOnly ? "checklist only" : "browsable"}
              title={option.prompt}
            />
          )}
          noOptionsMessage={() =>
            error ? "Error loading journal prompts" : loading ? "Loading..." : "No prompts in this category"
          }
          isSearchable
          isLoading={loading}
          isDisabled={loading || !selectedCategoryId}
          styles={detailSelectStyles}
        />
      </div>

      <div>
        <TextField
          label="Brief Prompt"
          name="journalPromptBrief"
          placeholder="what helps you reset after a hard week"
          description={`Completes the checklist label. ${
            brief ? brief.length : 0
          }/${journalPromptBriefMaxLength} characters.`}
          maxLength={journalPromptBriefMaxLength}
          required
        />
        <div style={{ fontSize: 12, color: "#666", paddingTop: 4 }}>
          Learners will see:{" "}
          <strong>
            {journalPromptTitlePrefix}
            {brief || "[brief prompt]"}
          </strong>
        </div>
      </div>

      {showReuseWarning && (
        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginTop: 8 } }}>
          This prompt is also browsable in the standard journal experience. Mark it "Checklist only" under
          Mindfulness &gt; Journals to keep it exclusive to this action item.
        </MessageBar>
      )}
    </>
  );
};

const MinimumScoreField = () => {
  const { getFieldProps, setFieldValue } = useFormikContext();
  const isChecked = !!getFieldProps("forceMinimumScore").value;

  return (
    <div>
      <Checkbox
        label="Force minimum score"
        checked={isChecked}
        onChange={(_, checked) => {
          setFieldValue("forceMinimumScore", checked);
          if (checked && !getFieldProps("minimumScore").value) {
            setFieldValue("minimumScore", 100);
          }
        }}
        styles={{ root: { padding: "5px 0" } }}
      />
      {isChecked && (
        <TextField
          label="Minimum Score (%)"
          placeholder="100"
          type="number"
          name="minimumScore"
          min={0}
          max={100}
          required
        />
      )}
    </div>
  );
};

export default ProprietarySpecificFields;
